/**
 * The studded toy brick seen at an angle (front, top and right side), drawn on a
 * canvas. Shared by the landing page's brick pile and Skyline Stacker.
 */

export interface IsoMetrics {
  /** Height of the front face. */
  bh: number;
  /** Horizontal and vertical depth of the top/side faces. */
  dx: number;
  dy: number;
}

export function isoMetrics(unit: number): IsoMetrics {
  return { bh: unit * 1.1, dx: unit * 0.6, dy: unit * 0.42 };
}

export interface IsoBrickStyle {
  /** Outlined look (Blueprint theme): faces filled with this color, edges in the brick's color. */
  outline?: string;
  /** Picked-up look: a soft drop shadow under the brick. */
  lift?: boolean;
}

export interface IsoBrick {
  /** Left edge of the front face, px. */
  x: number;
  /** Top edge of the front face, px. */
  y: number;
  /** Width of the front face, px (need not be a whole number of studs). */
  len: number;
  color: string;
}

function poly(ctx: CanvasRenderingContext2D, pts: number[], fill: string, stroke?: string | null) {
  ctx.beginPath();
  ctx.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 1.4;
    ctx.lineJoin = "round";
    ctx.stroke();
  }
}

/**
 * Draws one brick. Studs sit one per whole stud of width, centred, so a brick
 * cut to an odd width still looks right. Uses the context's current alpha.
 */
export function drawIsoBrick(ctx: CanvasRenderingContext2D, b: IsoBrick, unit: number, style: IsoBrickStyle = {}): void {
  const { bh, dx, dy } = isoMetrics(unit);
  const { x, y, len: w } = b;
  const line = style.outline ? b.color : null;
  const fill = style.outline || b.color;

  if (style.lift) {
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.28)";
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 10;
    poly(ctx, [x, y, x + w, y, x + w + dx, y - dy, x + w + dx, y - dy + bh, x + w, y + bh, x, y + bh], fill);
    ctx.restore();
  }

  const front = [x, y, x + w, y, x + w, y + bh, x, y + bh];
  const top = [x, y, x + w, y, x + w + dx, y - dy, x + dx, y - dy];
  const side = [x + w, y, x + w + dx, y - dy, x + w + dx, y - dy + bh, x + w, y + bh];
  if (line) {
    poly(ctx, front, fill, line);
    poly(ctx, top, fill, line);
    poly(ctx, side, fill, line);
  } else {
    poly(ctx, front, fill);
    poly(ctx, top, fill);
    poly(ctx, top, "rgba(255,255,255,0.28)");
    poly(ctx, side, fill);
    poly(ctx, side, "rgba(0,0,0,0.26)");
  }

  const studs = Math.floor(w / unit + 1e-6);
  const pad = (w - studs * unit) / 2;
  const rx = unit * 0.3, ry = rx * 0.55, h = unit * 0.24;
  for (let i = 0; i < studs; i++) {
    const cx = x + pad + i * unit + unit / 2 + dx / 2, cy = y - dy / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI);
    ctx.lineTo(cx - rx, cy - h);
    ctx.ellipse(cx, cy - h, rx, ry, 0, Math.PI, 0, true);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    if (line) {
      ctx.strokeStyle = line;
      ctx.lineWidth = 1.4;
      ctx.stroke();
    } else {
      ctx.fillStyle = "rgba(0,0,0,0.12)";
      ctx.fill();
    }
    ctx.beginPath();
    ctx.ellipse(cx, cy - h, rx, ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    if (line) ctx.stroke();
    else {
      ctx.fillStyle = "rgba(255,255,255,0.34)";
      ctx.fill();
    }
  }
}
